import Dexie from 'dexie';
import * as moment from 'moment';
import isEqual from 'lodash-es/isEqual';
import { logSystem } from 'logger';
import { db } from '../database';
import { encrypt_item, decrypt_item } from '../support/crypt';
export const UserStateRecordSchema = '++, [key+userid], changed';
export class UserStateRecord {
    key;
    userid;
    state;
    version;
    changed;
    constructor(key, userid, state, version = 0, changed) {
        this.key = key;
        this.userid = userid;
        this.state = state;
        this.version = version;
        this.changed = changed;
    }
    async save() {
        this.changed = moment().toISOString();
        this.version += 1;
        await db.userstate.put(this);
    }
    age() {
        const { changed } = this;
        if (changed) {
            const now = moment();
            const old = moment(changed);
            return now.diff(old, 'hours');
        }
        return 0;
    }
}
const pendingRefresh = new Map();
const waitForWS = () => {
    return new Promise((resolve) => {
        (function waitForIt() {
            if (window.hcstatic.sitews) {
                resolve(window.hcstatic.sitews);
            }
            else {
                setTimeout(waitForIt, 50);
            }
        })();
    });
};
async function processPendingRefresh() {
    const sitews = await waitForWS();
    sitews.on('open', () => {
        pendingRefresh.forEach((refresh, key) => {
            refresh();
            pendingRefresh.delete(key);
        });
    });
}
processPendingRefresh();
export async function check_session_id(sessionid) {
    const { portal_userid: userid = '' } = window.hcstatic;
    const key = 'session-id';
    let record = await db.userstate.where({ key, userid }).first();
    const sessid = record !== undefined ? +record.state : undefined;
    logSystem.info(() => `check_session_id(${sessionid}) for userid="${userid}"; old session-id=${sessid}`);
    if (sessid !== sessionid) {
        await purge_user_state();
        record = new UserStateRecord(key, userid, sessionid.toString());
        record.save();
    }
}
export async function purge_user_state() {
    const { portal_userid: userid = '' } = window.hcstatic;
    const count = await db.userstate
        .where('[key+userid]')
        .between([Dexie.minKey, userid], [Dexie.maxKey, userid])
        .delete();
    logSystem.info(() => `purge_user_state for ${userid}; purged ${count} records`);
    return count;
}
export async function set_user_state({ key, state, setstate }) {
    const keyval = key;
    const { portal_userid: userid = '' } = window.hcstatic;
    const refreshKey = `${keyval}+${userid}`;
    if (pendingRefresh.has(refreshKey)) {
        pendingRefresh.delete(refreshKey);
    }
    let record = await db.userstate.where({ key: keyval, userid }).first();
    const stateString = await encrypt_item({ _v_: state });
    if (record === undefined) {
        record = new UserStateRecord(keyval, userid, stateString);
        record.save();
    }
    else {
        record.state = stateString;
        record.save();
    }
    const { sitews } = window.hcstatic;
    if (sitews.is_open()) {
        await setstate({ key, state });
    }
    else {
        pendingRefresh.set(refreshKey, () => {
            setstate({ key, state });
        });
    }
}
export async function get_user_state({ key, getstate, statechange }) {
    const keyval = key;
    const { portal_userid: userid = '' } = window.hcstatic;
    let record = await db.userstate.where({ key: keyval, userid }).first();
    if (record === undefined) {
        const state = await getstate({ key });
        record = new UserStateRecord(keyval, userid, await encrypt_item({ _v_: state }));
        record.save();
    }
    else {
        const { sitews } = window.hcstatic;
        const refresh = async () => {
            const state = await getstate({ key });
            const recstate = await decrypt_item(record.state);
            if (!isEqual(state, recstate._v_)) {
                db.userstate.where({ key: keyval, userid }).modify({
                    changed: moment().toISOString(),
                    state: await encrypt_item({ _v_: state }),
                    version: record.version + 1
                });
                statechange && statechange(state);
            }
        };
        if (sitews.is_open()) {
            refresh();
        }
        else {
            const refreshKey = `${keyval}+${userid}`;
            pendingRefresh.set(refreshKey, refresh);
        }
    }
    const state = await decrypt_item(record.state);
    return state._v_;
}
export async function has_user_state(key) {
    const { portal_userid: userid = '' } = window.hcstatic;
    const record = await db.userstate.where({ key, userid }).first();
    return record !== undefined;
}
